import { knowledgeBaseClient } from '../lib/client';
import { KB_ENDPOINTS } from './knowledge-base.utils';
import type { ApiResponse } from '../lib/types';
import type {
    KnowledgeBase,
    KnowledgeBaseResponse,
    CreateKnowledgeBaseRequest,
    UpdateKnowledgeBaseRequest,
    GetKnowledgeBaseResponse
} from './knowledge-base.types';

/**
 * Get all knowledge bases for the organization
 */
export const getAllKnowledgeBases = async (): Promise<ApiResponse<KnowledgeBaseResponse>> => {
    try {
        const response = await knowledgeBaseClient.get(KB_ENDPOINTS.GET_ALL);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || error.message || 'Failed to fetch knowledge bases' };
    }
};

/**
 * Get a single knowledge base by ID
 */
export const getKnowledgeBaseById = async (id: string): Promise<ApiResponse<GetKnowledgeBaseResponse>> => {
    try {
        const response = await knowledgeBaseClient.get(KB_ENDPOINTS.GET_BY_ID(id));
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || error.message || 'Failed to fetch knowledge base' };
    }
};

/**
 * Create a new knowledge base
 */
export const createKnowledgeBase = async (payload: CreateKnowledgeBaseRequest): Promise<ApiResponse<KnowledgeBase>> => {
    try {
        const response = await knowledgeBaseClient.post(KB_ENDPOINTS.CREATE, payload);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || error.message || 'Failed to create knowledge base' };
    }
};

/**
 * Update an existing knowledge base
 * Note: API uses PUT, not PATCH
 */
export const updateKnowledgeBase = async (
    id: string,
    payload: UpdateKnowledgeBaseRequest
): Promise<ApiResponse<KnowledgeBase>> => {
    try {
        const response = await knowledgeBaseClient.put(KB_ENDPOINTS.UPDATE(id), payload);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || error.message || 'Failed to update knowledge base' };
    }
};

/**
 * Delete a knowledge base
 * Returns 204 No Content on success
 */
export const deleteKnowledgeBase = async (id: string): Promise<ApiResponse<void>> => {
    try {
        await knowledgeBaseClient.delete(KB_ENDPOINTS.DELETE(id));
        return { data: undefined };
    } catch (error: any) {
        return { error: error.response?.data?.message || error.message || 'Failed to delete knowledge base' };
    }
};
